// a11yPrefs — the accessibility preferences of the workspace app.
//
// The SAME four choices, the same names and the same data-attribute contract
// as the public site (company/packages/landing/functions/a11yPrefs.js), so a
// person who set their preferences on symbols.app finds the identical
// switches inside the product and they behave identically (2026-09-04 per
// Nika "add same accessibility options as we have in landing also in
// workspace settings"):
//
//   a11yFontScale  'sm' | 'md' | 'lg'   one type-scale step down / base / up
//   a11yContrast   boolean              quiet inks become the ink
//   a11yMotion     boolean              transitions and animations stand still
//   a11yLinks      boolean              every link shows its underline
//
// PER PERSON, not per workspace. These live in localStorage on the device,
// like `themeMode` — an admin forcing reduced motion on a whole workspace
// would be the opposite of an accessibility feature. The card sits on the
// account settings page beside Language, which is the same kind of choice.
//
// The shell mirrors the four onto <html> as `data-font-scale` /
// `data-contrast` / `data-motion` / `data-links` (workspace/app.js
// `_syncShellRoute`), and the injected shell sheet scopes the global rules on
// them — the same attribute names the landing's designSystem/reset.js uses.
//
// ⚠ EVERY module-level name here is prefixed `WS_A11Y_`. The published bundle
// puts every functions/*.js file into ONE shared scope, so a bare
// `A11Y_STORAGE_KEY` would resolve to whichever sibling declared it last — the
// landing lost its prefs to keepPlace.js exactly that way (measured
// 2026-09-03, see that file's own note).
const WS_A11Y_STORAGE_KEY = 'workspace-a11y'
const WS_A11Y_SCALES = ['sm', 'md', 'lg']
const WS_A11Y_FLAGS = ['a11yContrast', 'a11yMotion', 'a11yLinks']

const wsA11yWindow = (win) => win || (typeof window !== 'undefined' ? window : null)

const wsA11yRead = (win) => {
  try {
    const raw = win.localStorage.getItem(WS_A11Y_STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : null
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch (err) {
    return {}
  }
}

const wsA11yWrite = (win, prefs) => {
  try {
    win.localStorage.setItem(WS_A11Y_STORAGE_KEY, JSON.stringify(prefs))
  } catch (err) {}
}

// stored shape → root-state keys, defaults for anything missing
const wsA11yToState = (stored) => ({
  a11yFontScale: WS_A11Y_SCALES.indexOf(stored.fontScale) !== -1 ? stored.fontScale : 'md',
  a11yContrast: stored.contrast === true,
  a11yMotion: stored.motion === true,
  a11yLinks: stored.links === true
})

// root-state keys → stored shape
const wsA11yToStored = (state) => ({
  fontScale: WS_A11Y_SCALES.indexOf(state?.a11yFontScale) !== -1 ? state.a11yFontScale : 'md',
  contrast: !!state?.a11yContrast,
  motion: !!state?.a11yMotion,
  links: !!state?.a11yLinks
})

// The <html> attribute mirror, applied by THIS module — whatever owns the
// preference owns its DOM projection.
//
// Deliberately NOT the shell's `_syncShellRoute`: that runs on route change
// and a few subscribed keys, so a preference written from the settings card
// persisted correctly and painted NOTHING until the next navigation (measured
// live 2026-09-04 — localStorage held {"fontScale":"lg"} while <html> carried
// no attribute at all). Writing here also gives the canvas app, which serves
// this same settings page and has no `_syncShellRoute`, identical behaviour.
const wsA11yMirror = (root, win) => {
  const doc = (win && win.document) || (typeof document !== 'undefined' ? document : null)
  if (!doc || !doc.documentElement) return
  const attrs = a11yRootAttrs(root)
  for (const name of Object.keys(attrs)) {
    const value = attrs[name]
    if (value === null) doc.documentElement.removeAttribute(name)
    else doc.documentElement.setAttribute(name, value)
  }
}

// The <html> attribute set the shell sheet scopes its rules on.
// `null` means "remove the attribute": the base rules ARE the md / normal /
// animated / no-underline case, so an attribute at the default value would be
// dead weight in every DOM inspection. Same convention the landing uses.
export const a11yRootAttrs = (root) => ({
  'data-font-scale':
    root?.a11yFontScale === 'sm' || root?.a11yFontScale === 'lg' ? root.a11yFontScale : null,
  'data-contrast': root?.a11yContrast ? 'high' : null,
  'data-motion': root?.a11yMotion ? 'reduced' : null,
  'data-links': root?.a11yLinks ? 'underline' : null
})

// Read the stored choices into root state. Called once at shell boot, BEFORE
// the first paint, so a person who chose larger type never sees one frame of
// the base scale. Returns the applied slice.
export const applyA11yPrefs = (root, win) => {
  const w = wsA11yWindow(win)
  if (!root || !w) return null
  const next = wsA11yToState(wsA11yRead(w))
  if (typeof root.update === 'function') root.update(next, { preventFetch: true })
  else Object.assign(root, next)
  wsA11yMirror(next, w)
  return next
}

// Write one preference and persist the whole set. Unknown keys are refused
// rather than written through — this is the only writer, so a typo at a call
// site must not create a phantom root key nobody reads (the class of bug
// WS-ROOT-DEFAULTS-NO-WRITER-1 catalogues from the other direction).
export const setA11yPref = (root, key, value, win) => {
  const w = wsA11yWindow(win)
  if (!root) return null
  const patch = {}
  if (key === 'a11yFontScale') {
    patch[key] = WS_A11Y_SCALES.indexOf(value) !== -1 ? value : 'md'
  } else if (WS_A11Y_FLAGS.indexOf(key) !== -1) {
    patch[key] = !!value
  } else {
    return null
  }
  if (typeof root.update === 'function') root.update(patch, { preventFetch: true })
  else Object.assign(root, patch)
  const merged = { ...(typeof root.parse === 'function' ? root.parse() : root), ...patch }
  const stored = wsA11yToStored(merged)
  if (w) wsA11yWrite(w, stored)
  wsA11yMirror(merged, w)
  return stored
}

// Back to the shipped defaults, in one action — the row every preferences
// panel owes a person who has changed three things and wants out.
export const resetA11yPrefs = (root, win) => {
  const w = wsA11yWindow(win)
  if (!root) return null
  const next = wsA11yToState({})
  if (typeof root.update === 'function') root.update(next, { preventFetch: true })
  else Object.assign(root, next)
  if (w) wsA11yWrite(w, wsA11yToStored(next))
  wsA11yMirror(next, w)
  return next
}

// True when any choice is off the shipped default — the Reset row hides
// otherwise (Rule 66: stay mounted, only hide).
export const a11yPrefsAreDefault = (root) => {
  const s = wsA11yToStored(root || {})
  return s.fontScale === 'md' && !s.contrast && !s.motion && !s.links
}
